import { findTemplateTokenAtCursor } from "../app/templates/templateFormulaOptions";
import type { TemplateVariableEditorTarget } from "./TemplateVariableToolbar";

/** Namen der Textmarker (`[b]`, `[color=…]`), wie der Server sie erlaubt. */
const MARKER_BR = "br";
const MARKER_BOLD = "b";
const MARKER_ITALIC = "i";
const MARKER_UNDERLINE = "u";
const MARKER_COLOR = "color";
const MARKER_BACKGROUND = "background";
const MARKER_FONT = "font";
const MARKER_SIZE = "size";

/** `tagName`/`nodeName` der Elemente, die der Inline-Editor erzeugt. */
const TAG_BR = "BR";
const TAG_STRONG = "STRONG";
const TAG_B = "B";
const TAG_EM = "EM";
const TAG_I = "I";
const TAG_U = "U";
const TAG_FONT = "FONT";

const HEX_RADIX = 16;
/** Hex-Stellen je Farbkanal (`ff`). */
const HEX_CHANNEL_WIDTH = 2;

// Gemeinsamer Speichervertrag mit
// backend/Services/Settings/Documents/SystemDocumentRichText.cs — beide Kataloge zusammen ändern.
const markerExpression = /\[(?<close>\/)?(?<name>b|i|u|br|color|background|font|size)(?:=(?<value>[^\]]+))?\]/g;
/** Gleicher Link-Ausdruck wie `TemplateVariableService.TemplateLinkExpressionRegex`. */
const linkExpression = /^(?<key>[A-Za-z][A-Za-z0-9_]*)\s+link(?:\s*=\s*(?:"(?<double>[^"]+)"|'(?<single>[^']+)'|&quot;(?<html>.+?)&quot;))?$/i;

const escapeHtml = (value: string): string => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

function renderRichTextFragment(value: string): string {
  let html = "";
  let position = 0;
  const open: string[] = [];

  for (const marker of value.matchAll(markerExpression)) {
    html += escapeHtml(value.slice(position, marker.index)).replace(/\r?\n/g, "<br>");
    const name = marker.groups?.name?.toLowerCase();
    const closing = marker.groups?.close != null;
    if (!name) continue;

    if (name === MARKER_BR) {
      if (!closing && marker.groups?.value == null) {
        // Ein einfacher Zeilenumbruch bleibt innerhalb desselben Absatzes. Das Datenattribut
        // unterscheidet ihn beim Rückweg vom unsichtbaren Platzhalter eines leeren Absatzes.
        html += '<br data-mq-soft-break="true">';
      } else {
        html += escapeHtml(marker[0]);
      }
    } else if (closing && open[open.length - 1] === name) {
      open.pop();
      html += name === MARKER_BOLD ? "</strong>" : name === MARKER_ITALIC ? "</em>" : name === MARKER_UNDERLINE ? "</u>" : "</span>";
    } else if (!closing && (!(name === MARKER_COLOR || name === MARKER_BACKGROUND || name === MARKER_FONT || name === MARKER_SIZE) || marker.groups?.value)) {
      open.push(name);
      const val = marker.groups?.value ?? "";
      html += name === MARKER_BOLD
        ? "<strong>"
        : name === MARKER_ITALIC
          ? "<em>"
          : name === MARKER_UNDERLINE
            ? "<u>"
            : name === MARKER_COLOR
              ? `<span data-mq-color="${val.toLowerCase()}" style="color:${val.toLowerCase()}">`
              : name === MARKER_BACKGROUND
                ? `<span data-mq-background="${val.toLowerCase()}" style="background-color:${val.toLowerCase()}">`
                : name === MARKER_FONT
                  ? `<span data-mq-font="${escapeHtml(val)}" style="font-family:${escapeHtml(val)}">`
                  : `<span data-mq-size="${escapeHtml(val)}" style="font-size:${escapeHtml(val)}">`;
    } else {
      html += escapeHtml(marker[0]);
    }
    position = (marker.index ?? 0) + marker[0].length;
  }

  html += escapeHtml(value.slice(position)).replace(/\r?\n/g, "<br>");
  while (open.length) {
    const name = open.pop();
    html += name === MARKER_BOLD ? "</strong>" : name === MARKER_ITALIC ? "</em>" : name === MARKER_UNDERLINE ? "</u>" : "</span>";
  }
  return html;
}

/** Übersetzt ausschließlich die serverseitig erlaubten Textmarker für den Inline-Editor. */
export function systemDocumentValueToHtml(value: string): string {
  const normalized = value.replace(/\r\n/g, "\n");
  const paragraphs = normalized.split("\n");
  return paragraphs.map((p, idx) => {
    const isLast = idx === paragraphs.length - 1;
    const content = p.length === 0 ? "<br>" : renderRichTextFragment(p);
    return isLast
      ? `<p style="margin-bottom:0;">${content}</p>`
      : `<p>${content}</p>`;
  }).join("");
}

function toHexColor(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value.toLowerCase();
  const match = value.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  return match ? `#${match.slice(1).map((channel) => Number(channel).toString(HEX_RADIX).padStart(HEX_CHANNEL_WIDTH, "0")).join("")}` : null;
}

const LINE_ELEMENT_NAMES = new Set(["DIV", "P"]);

function childrenToValue(parent: ParentNode, stripTrailingPlaceholder = false): string {
  const children = Array.from(parent.childNodes);
  const last = children[children.length - 1];
  // Nur das alleinstehende <br> eines leeren Absatzes ist ein Browser-Platzhalter. Nach Text ist
  // ein abschließendes <br> ein wirklicher, mit Shift+Enter erzeugter Zeilenumbruch.
  const trailingPlaceholder = stripTrailingPlaceholder
    && last?.nodeName === TAG_BR
    && children.slice(0, -1).every((child) => (child.textContent ?? "").length === 0);
  const meaningful = trailingPlaceholder ? children.slice(0, -1) : children;
  let value = "";
  let hasVisualContent = false;

  for (const child of meaningful) {
    const element = child.nodeType === Node.ELEMENT_NODE ? child as HTMLElement : null;
    const startsLine = element != null && LINE_ELEMENT_NAMES.has(element.tagName);
    // Der Zustand darf nicht aus `value.length` abgeleitet werden: Ein leerer Absatz hat bewusst
    // keinen Text, ist aber trotzdem eine vorhandene Zeile. Sonst verschwinden führende und
    // verschachtelte Leerzeilen beim nächsten Rendern.
    if (startsLine && hasVisualContent) value += "\n";
    value += nodeToValue(child);
    hasVisualContent = hasVisualContent || startsLine || (child.textContent?.length ?? 0) > 0 || child.nodeName === TAG_BR;
  }

  return value;
}

function nodeToValue(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replace(/\u00a0/g, " ");
  // Absatzgrenzen werden separat als `\n` geschrieben. Ein <br> ist dagegen der weiche
  // Zeilenumbruch innerhalb eines Absatzes und braucht einen eigenen kanonischen Marker.
  if (node.nodeName === TAG_BR) return "[br]";
  if (node.nodeType !== 1) return "";
  const element = node as HTMLElement;
  if (element.dataset.mqTemplateToken != null) return element.dataset.mqTemplateToken;

  // Chromium setzt neue Zeilen je nach Ausgangsmarkup sowohl direkt als auch verschachtelt als
  // <div>/<p> ein. Die Zeilentrennung muss deshalb auf jeder Ebene erhalten bleiben.
  const children = childrenToValue(element, LINE_ELEMENT_NAMES.has(element.tagName));
  if (element.tagName === TAG_STRONG || element.tagName === TAG_B) return `[b]${children}[/b]`;
  if (element.tagName === TAG_EM || element.tagName === TAG_I) return `[i]${children}[/i]`;
  if (element.tagName === TAG_U) return `[u]${children}[/u]`;

  const font = element.dataset.mqFont ?? (element.tagName === TAG_FONT ? element.getAttribute("face") : null) ?? (element.style.fontFamily ? element.style.fontFamily.replace(/["']/g, "") : null);
  const size = element.dataset.mqSize ?? (element.style.fontSize && element.style.fontSize.endsWith("pt") ? element.style.fontSize : null);
  const color = toHexColor(element.dataset.mqColor ?? (element.tagName === TAG_FONT ? element.getAttribute("color") : null) ?? element.style.color);
  const background = toHexColor(element.dataset.mqBackground ?? element.style.backgroundColor);

  let result = children;
  if (color) result = `[color=${color}]${result}[/color]`;
  if (background) result = `[background=${background}]${result}[/background]`;
  if (size) result = `[size=${size}]${result}[/size]`;
  if (font) result = `[font=${font}]${result}[/font]`;
  return result;
}

/**
 * Einmal die Eingabetaste ist eine neue Zeile, zweimal eine Leerzeile — genau so, wie es beim
 * Schreiben aussieht. Zuvor wurde aus jedem Absatz ein doppelter Umbruch; im Anschriftenfeld riss
 * das die Adresse nach jeder Zeile auseinander.
 */
export function systemDocumentHtmlToValue(root: HTMLElement): string {
  return childrenToValue(root);
}

type ParagraphPresentation = {
  style: string | null;
  spacing: string | null;
};

/**
 * Bewahrt ausschließlich die vom Renderer gesetzte Absatzdarstellung, wenn der token-sichere
 * Vorschauwert lokal wieder in den Rohtext eingesetzt werden muss. Der Wortlaut wird bewusst
 * nicht übernommen: Er stammt weiterhin aus `source` und dem geschützten Token.
 */
function paragraphPresentation(root: HTMLElement): ParagraphPresentation[] {
  return Array.from(root.querySelectorAll<HTMLElement>("p")).map((paragraph) => ({
    style: paragraph.getAttribute("style"),
    spacing: paragraph.getAttribute("data-mq-paragraph-spacing"),
  }));
}

function applyParagraphPresentation(root: HTMLElement, presentation: ParagraphPresentation[]): void {
  if (presentation.length === 0) return;
  const paragraphs = Array.from(root.querySelectorAll<HTMLElement>("p"));
  paragraphs.forEach((paragraph, index) => {
    // Bei gleicher Absatzanzahl ist die Zuordnung exakt. Löst ein Musterwert zusätzliche Zeilen
    // auf, bleiben zumindest erster und letzter Absatz an den entsprechenden Außenkanten.
    const sourceIndex = presentation.length === paragraphs.length
      ? index
      : index === paragraphs.length - 1 ? presentation.length - 1 : Math.min(index, presentation.length - 1);
    const source = presentation[sourceIndex];
    if (source == null) return;
    if (source.style == null) paragraph.removeAttribute("style");
    else paragraph.setAttribute("style", source.style);
    if (source.spacing == null) paragraph.removeAttribute("data-mq-paragraph-spacing");
    else paragraph.setAttribute("data-mq-paragraph-spacing", source.spacing);
  });
}

type TemplatePreviewReplacement = {
  text: string;
  href?: string;
};

function isSafePreviewHref(href: string): boolean {
  const value = href.trim().toLowerCase();
  return value.startsWith("https:") || value.startsWith("http:") || value.startsWith("mailto:");
}

function normalizePreviewHref(key: string, value: string): string {
  const href = value.trim();
  if (key.toLowerCase().includes("email")) {
    return href.toLowerCase().startsWith("mailto:") ? href : `mailto:${href}`;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return href;
  if (href.startsWith("//")) return `https:${href}`;
  return `https://${href.replace(/^\/+/, "")}`;
}

function previewLinkLabel(key: string, raw: string, explicit: string | undefined): string {
  const inferred = raw.trim();
  let label = (explicit ?? inferred).trim();
  if (key.toLowerCase().includes("email") && label.toLowerCase().startsWith("mailto:")) {
    label = label.slice("mailto:".length);
  }
  return label.length > 0 ? label : inferred;
}

/**
 * Löst einen Token-Inhalt gegen die Vorschauwerte auf. `{{editBookingUrl link="hier"}}` hat
 * keinen eigenen Kontextschlüssel; der Wert sitzt an `editBookingUrl`.
 */
function previewReplacement(
  expression: string,
  templateValues: Readonly<Record<string, string>>,
): TemplatePreviewReplacement | null {
  const trimmed = expression.trim();
  const direct = templateValues[trimmed];
  if (direct != null) return { text: direct };

  const match = linkExpression.exec(trimmed);
  if (match == null) return null;
  const key = match.groups?.key;
  if (key == null) return null;
  const raw = templateValues[key];
  if (raw == null || raw.length === 0) return null;

  const explicit = match.groups?.double ?? match.groups?.single ?? match.groups?.html;
  const text = previewLinkLabel(key, raw, explicit);
  const href = normalizePreviewHref(key, raw);
  return isSafePreviewHref(href) ? { text, href } : { text };
}

function createPreviewValueElement(
  document: Document,
  token: string,
  resolved: TemplatePreviewReplacement,
): HTMLElement {
  const valueElement = document.createElement(resolved.href != null ? "a" : "span");
  valueElement.className = "mq-template-value";
  valueElement.contentEditable = "false";
  valueElement.dataset.mqTemplateToken = token;
  valueElement.title = token;
  valueElement.textContent = resolved.text;
  if (resolved.href != null) valueElement.setAttribute("href", resolved.href);
  return valueElement;
}

/**
 * Zeigt aufgelöste Vorschauwerte, behält aber den eigentlichen Platzhalter als geschütztes Token.
 * Beim Auslesen liefert `nodeToValue` wieder genau diesen Token. Damit wird kein Musterwert in den
 * gespeicherten Text geschrieben. Linkformeln werden als Anker eingesetzt, nicht als sichtbare Tags.
 */
export function applyTemplateValuePreview(
  root: HTMLElement,
  source: string,
  templateValues: Readonly<Record<string, string>>,
): void {
  const tokenExpression = /{{\s*([^{}]+?)\s*}}/g;
  const tokens = Array.from(source.matchAll(tokenExpression));
  // Datenblöcke (Tabellen/Aufstellungen) und Rechenformeln haben keinen einzelnen Kontextwert. Sie
  // bleiben in der serverseitigen Vorschau: Sonst würde eine Tabelle beim Schreiben zu Text.
  // Text ohne Variable bleibt vollständig im serverseitigen Renderer. Ihn hier neu aufzubauen
  // würde dessen Absatz- und Blockstruktur verändern, obwohl der Schalter gar nichts zu tun hat.
  if (tokens.length === 0 || tokens.some((match) => previewReplacement(match[1], templateValues) == null)) return;

  // Im Roh-Render stehen die Tokens bereits in der vom Server formatierten Absatzstruktur. Diese
  // Struktur bleibt erhalten; insbesondere gehen die konfigurierten Brief-/Mail-Abstände nicht
  // durch neu erzeugte <p>-Elemente mit Browser-Standardrändern verloren. Nur ältere/anderweitig
  // bereits aufgelöste Renderpfade brauchen den sicheren Wiederaufbau aus der Quelle.
  if (!/{{\s*[^{}]+?\s*}}/.test(root.textContent ?? "")) {
    const presentation = paragraphPresentation(root);
    root.innerHTML = systemDocumentValueToHtml(source);
    applyParagraphPresentation(root, presentation);
  }
  const document = root.ownerDocument;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) nodes.push(node as Text);

  for (const textNode of nodes) {
    const value = textNode.data;
    tokenExpression.lastIndex = 0;
    if (!tokenExpression.test(value)) continue;
    tokenExpression.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let position = 0;
    for (const match of value.matchAll(tokenExpression)) {
      const index = match.index ?? 0;
      fragment.append(value.slice(position, index));
      const token = match[0];
      const resolved = previewReplacement(match[1], templateValues);
      if (resolved == null) {
        fragment.append(token);
      } else {
        fragment.append(createPreviewValueElement(document, token, resolved));
      }
      position = index + token.length;
    }
    fragment.append(value.slice(position));
    textNode.replaceWith(fragment);
  }
}

/** Zeigt für die Bearbeitung wieder den Rohtext, aber nur wenn er tatsächlich Variablen enthält. */
export function applyTemplateSource(root: HTMLElement, source: string): void {
  if (!/{{\s*[^{}]+?\s*}}/.test(source)) return;
  if (!/{{\s*[^{}]+?\s*}}/.test(root.textContent ?? "")) {
    root.innerHTML = systemDocumentValueToHtml(source);
  }
}

function textNodes(root: HTMLElement): Text[] {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) nodes.push(node as Text);
  return nodes;
}

function selectionOffset(root: HTMLElement): number | null {
  const selection = root.ownerDocument.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;
  const before = root.ownerDocument.createRange();
  before.selectNodeContents(root);
  before.setEnd(range.startContainer, range.startOffset);
  return before.toString().length;
}

/** Der Toolbar-Vertrag für genau den aktuell angeklickten Absatz in der Dokumentvorschau. */
/**
 * Die zuletzt bekannte Schreibmarke je Schreibfläche.
 *
 * Nötig, weil die Werkzeugleiste außerhalb des Dokuments liegt: Der Klick auf „Variable einfügen"
 * nimmt der Fläche den Fokus, und mit ihm verschwindet die Auswahl. Ohne diese Erinnerung landet
 * das Eingefügte am Anfang — oder nirgends, weil es gar keine Auswahl mehr gibt.
 */
const lastSelections = new WeakMap<HTMLElement, Range>();

/** Merkt sich die Schreibmarke der Fläche, solange sie noch besteht. */
export function rememberInlineSelection(editor: HTMLElement): void {
  const selection = editor.ownerDocument.getSelection();
  const range = selection != null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  if (range != null && editor.contains(range.commonAncestorContainer)) {
    lastSelections.set(editor, range.cloneRange());
  }
}

/**
 * Ob in der Schreibfläche tatsächlich Text markiert ist.
 *
 * Ein Zeichenformat braucht eine Markierung. Steht dort nur die Schreibmarke, setzt ein
 * Editorkommando lediglich den Zustand für das **nächste** getippte Zeichen — sichtbar ändert sich
 * nichts, und der Klick auf „fett" sieht wirkungslos aus. Genau dann gilt die Auszeichnung dem
 * ganzen Baustein, und der Aufrufer muss das unterscheiden können.
 *
 * Geprüft wird die gemerkte Markierung: Die lebende ist bereits zusammengefallen, sobald die
 * Werkzeugleiste den Fokus bekommen hat.
 */
export function hasInlineSelection(editor: HTMLElement): boolean {
  const remembered = lastSelections.get(editor);
  if (remembered != null && editor.contains(remembered.commonAncestorContainer)) {
    return !remembered.collapsed;
  }
  const selection = editor.ownerDocument.getSelection();
  const range = selection != null && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  return range != null && !range.collapsed && editor.contains(range.commonAncestorContainer);
}

/**
 * Stellt die zuletzt gemerkte Markierung wieder her.
 *
 * `focus()` allein genügt nicht: Nach einem Klick in die Werkzeugleiste ist die Markierung
 * zusammengefallen, und ein Format landete dann auf einer leeren Schreibmarke statt auf dem Wort,
 * das noch blau hinterlegt aussieht.
 */
export function restoreInlineSelection(editor: HTMLElement): void {
  editor.focus();
  const range = lastSelections.get(editor);
  if (range == null) return;
  const selection = editor.ownerDocument.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

type InlineColorKind = "text" | "background";

function carriesInlineColor(element: HTMLElement, kind: InlineColorKind): boolean {
  if (kind === "text") {
    return element.dataset.mqColor != null
      || (element.tagName === TAG_FONT && element.hasAttribute("color"))
      || element.style.color.length > 0;
  }
  return element.dataset.mqBackground != null || element.style.backgroundColor.length > 0;
}

function removeInlineColor(element: HTMLElement, kind: InlineColorKind): void {
  if (kind === "text") {
    delete element.dataset.mqColor;
    if (element.tagName === TAG_FONT) element.removeAttribute("color");
    element.style.removeProperty("color");
  } else {
    delete element.dataset.mqBackground;
    element.style.removeProperty("background-color");
  }
  if (element.getAttribute("style")?.trim().length === 0) element.removeAttribute("style");
}

/**
 * Hebt die markierte Teilstruktur aus farbtragenden Vorfahren heraus. Vor und nach der Markierung
 * bleiben geklonte, weiterhin farbige Hüllen stehen; die Mitte behält alle anderen Formatierungen.
 */
function liftOutsideInlineColor(marker: HTMLElement, editor: HTMLElement, kind: InlineColorKind): void {
  while (true) {
    let colorAncestor: HTMLElement | null = marker.parentElement;
    while (colorAncestor != null && colorAncestor !== editor && !carriesInlineColor(colorAncestor, kind)) {
      colorAncestor = colorAncestor.parentElement;
    }
    if (colorAncestor == null || colorAncestor === editor) return;

    let unit: HTMLElement = marker;
    while (unit.parentElement != null && unit.parentElement !== colorAncestor) {
      const parent = unit.parentElement;
      const before = parent.cloneNode(false) as HTMLElement;
      const middle = parent.cloneNode(false) as HTMLElement;
      const after = parent.cloneNode(false) as HTMLElement;
      while (parent.firstChild != null && parent.firstChild !== unit) before.appendChild(parent.firstChild);
      middle.appendChild(unit);
      while (parent.firstChild != null) after.appendChild(parent.firstChild);
      parent.replaceWith(
        ...(before.childNodes.length > 0 ? [before] : []),
        middle,
        ...(after.childNodes.length > 0 ? [after] : []),
      );
      unit = middle;
    }

    const before = colorAncestor.cloneNode(false) as HTMLElement;
    const middle = colorAncestor.cloneNode(false) as HTMLElement;
    const after = colorAncestor.cloneNode(false) as HTMLElement;
    while (colorAncestor.firstChild != null && colorAncestor.firstChild !== unit) before.appendChild(colorAncestor.firstChild);
    colorAncestor.removeChild(unit);
    while (colorAncestor.firstChild != null) after.appendChild(colorAncestor.firstChild);
    // Die mittlere Hülle behält Schriftart, Größe, Auszeichnungen und sonstige Darstellung des
    // ursprünglichen Elements; ausschließlich die gewählte Farbart fällt weg.
    removeInlineColor(middle, kind);
    middle.appendChild(unit);
    colorAncestor.replaceWith(
      ...(before.childNodes.length > 0 ? [before] : []),
      middle,
      ...(after.childNodes.length > 0 ? [after] : []),
    );
  }
}

function applyInlineColor(editor: HTMLElement | null, kind: InlineColorKind, color: string | null): boolean {
  if (editor == null || !hasInlineSelection(editor)) return false;
  restoreInlineSelection(editor);
  const doc = editor.ownerDocument;

  if (color != null) {
    executeInlineCommand(editor, kind === "text" ? "foreColor" : "backColor", color);
    return true;
  }

  const selection = doc.getSelection();
  if (selection == null) return false;
  const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
  if (range == null || range.collapsed || !editor.contains(range.commonAncestorContainer)) return false;

  const extracted = range.extractContents();
  extracted.querySelectorAll<HTMLElement>("*").forEach((element) => removeInlineColor(element, kind));
  const marker = doc.createElement("span");
  marker.dataset.mqInlineColorReset = kind;
  marker.appendChild(extracted);
  range.insertNode(marker);
  liftOutsideInlineColor(marker, editor, kind);

  const nextRange = doc.createRange();
  nextRange.selectNodeContents(marker);
  selection.removeAllRanges();
  selection.addRange(nextRange);
  lastSelections.set(editor, nextRange.cloneRange());
  return true;
}

/**
 * Führt ein Browser-Editorkommando aus, ohne zusätzlich das native `input` an den Dokument-Canvas
 * weiterzugeben. Der Aufrufer meldet den fertig normalisierten Inhalt anschließend genau einmal.
 * Einige Browser feuern für `execCommand` selbst ein `input`; zusammen mit dem bewussten Callback
 * entstanden sonst zwei Vorschauanforderungen und ein sichtbares doppeltes Neuladen des iframe.
 */
export function executeInlineCommand(editor: HTMLElement, command: string, value?: string | null): boolean {
  const suppressNativeInput = (event: Event) => event.stopImmediatePropagation();
  editor.addEventListener("input", suppressNativeInput, true);
  try {
    return editor.ownerDocument.execCommand(command, false, value ?? undefined);
  } finally {
    editor.removeEventListener("input", suppressNativeInput, true);
  }
}

/** Setzt die Textfarbe der Markierung oder entfernt sie zugunsten der geerbten Standardfarbe. */
export function applyInlineTextColor(editor: HTMLElement | null, color: string | null): boolean {
  return applyInlineColor(editor, "text", color);
}

/** Setzt die Hintergrundfarbe der Markierung oder entfernt sie vollständig. */
export function applyInlineBackgroundColor(editor: HTMLElement | null, color: string | null): boolean {
  return applyInlineColor(editor, "background", color);
}

export function createInlineTextTarget(editor: HTMLElement, emitChange: () => void): TemplateVariableEditorTarget {
  const restoreSelection = () => restoreInlineSelection(editor);
  return {
    insert: (next) => {
      restoreSelection();
      executeInlineCommand(editor, "insertText", next);
      emitChange();
    },
    tokenAtCaret: () => {
      const offset = selectionOffset(editor);
      return offset == null ? null : findTemplateTokenAtCursor(textNodes(editor).map((node) => node.data).join(""), offset);
    },
    replaceToken: (token, next) => {
      const nodes = textNodes(editor);
      let cursor = 0;
      let start: { node: Text; offset: number } | null = null;
      let end: { node: Text; offset: number } | null = null;
      for (const node of nodes) {
        const nodeEnd = cursor + node.data.length;
        if (!start && token.start >= cursor && token.start <= nodeEnd) start = { node, offset: token.start - cursor };
        if (token.end >= cursor && token.end <= nodeEnd) {
          end = { node, offset: token.end - cursor };
          break;
        }
        cursor = nodeEnd;
      }
      if (!start || !end) return;
      const range = editor.ownerDocument.createRange();
      range.setStart(start.node, start.offset);
      range.setEnd(end.node, end.offset);
      range.deleteContents();
      range.insertNode(editor.ownerDocument.createTextNode(next));
      range.collapse(false);
      const selection = editor.ownerDocument.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      emitChange();
    },
  };
}

export function applyInlineFontFamily(editor: HTMLElement, font: string | null): boolean {
  if (!hasInlineSelection(editor)) return false;
  restoreInlineSelection(editor);
  executeInlineCommand(editor, "fontName", font || "inherit");
  return true;
}

export function applyInlineFontSize(editor: HTMLElement, fontSizePt: number | null): boolean {
  if (!hasInlineSelection(editor)) return false;
  restoreInlineSelection(editor);
  const doc = editor.ownerDocument;
  const selection = doc.getSelection();
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);

  const span = doc.createElement("span");
  if (fontSizePt != null) {
    span.dataset.mqSize = `${fontSizePt}pt`;
    span.style.fontSize = `${fontSizePt}pt`;
  } else {
    span.style.fontSize = "inherit";
  }
  span.appendChild(range.extractContents());
  range.insertNode(span);
  selection.selectAllChildren(span);
  return true;
}
